import { useState, useEffect } from 'react';
import { useHistory, useParams, Link } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import Select from 'react-select';
import './Create.css';


const SubContractEdit = () => {
    const history = useHistory();
    const { id } = useParams();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(true);

    // Logout if token is expired
    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, token, logout]);

    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const [formData, setFormData] = useState({
        Id: '',
        CompanyId: companyId,
        Name: '',
        ContactNumber: '',
        StateId: '',
        PurchaseId: 0,
        CreatedBy: userId,
        CreatedDate: new Date().toISOString(),
    });

    // Fetch existing record data
    useEffect(() => {
        const fetchRecordData = async () => {
            try {
                setIsLoadingData(true);

                const response = await fetchData(
                    'SubContractAPI',
                    `/SubContract/${id}`,
                    token
                );

                if (response.responseCode === '1111' && response.response_data) {
                    const data = response.response_data;

                    setFormData({
                        Id: data.Id,
                        CompanyId: data.CompanyId,
                        Name: data.Name || '',
                        ContactNumber: data.ContactNumber || '',
                        StateId: data.StateId || '',
                        PurchaseId: data.PurchaseId || 0,
                        CreatedBy: data.CreatedBy,
                        CreatedDate: data.CreatedDate,
                    });

                    // Set the selected state after data is loaded
                    if (data.StateId) {
                        // Fetch the state name from the StateOptions once they're loaded
                        // For now, just set the StateId and let the useEffect handle the label
                        setFormData(prev => ({ ...prev, StateId: data.StateId }));
                    }
                }

                setIsLoadingData(false);
            } catch (error) {
                console.error('Error fetching record:', error);
                setErrorMessage('Failed to load record data.');
                setIsLoadingData(false);
            }
        };

        if (id) {
            fetchRecordData();
        }
    }, [id, token]);

    const submitData = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!formData.Name?.trim()) {
        setErrorMessage('Name is required.');
        return;
    }

    if (!formData.ContactNumber?.trim()) {
        setErrorMessage('Contact Number is required.');
        return;
    }

    if (!formData.StateId) {
        setErrorMessage('State is required.');
        return;
    }

    setIsLoading(true);

    try {
        const dataToSubmit = {
            Id: parseInt(formData.Id, 10),
            CompanyId: parseInt(companyId, 10),
            Name: formData.Name.trim(),
            ContactNumber: formData.ContactNumber.trim(),
            StateId: parseInt(formData.StateId, 10),
            PurchaseId: parseInt(formData.PurchaseId, 10) || 0,
            Item: formData.Name.trim(),
            CreatedBy: parseInt(formData.CreatedBy, 10),
            UpdatedBy: parseInt(userId, 10),
            CreatedDate: formData.CreatedDate,
            UpdatedDate: new Date().toISOString(),
            State:"Kerala",
        };

        console.log('Updating data:', dataToSubmit);
        console.log('URL will be: SubContractAPI/' + id);
        const response = await updateData('SubContractAPI', id, dataToSubmit, token);
        console.log('Data Updated success:', response);

        setSuccessMessage('SubContract updated successfully!');
        setTimeout(() => {
            history.push('/SubContractIndex');
        }, 1500);

    } catch (error) {
        console.error('Error:', error);

        if (error.response?.data) {
            const apiError = error.response.data;

            if (apiError.message) {
                setErrorMessage(apiError.message);
            } else if (apiError.errors) {
                const errorMessages = Object.values(apiError.errors).flat().join(', ');
                setErrorMessage(errorMessages);
            } else {
                setErrorMessage('An error occurred. Please try again.');
            }
        } else {
            setErrorMessage('An error occurred. Please try again.');
        }

        setIsLoading(false);
    }
};


    const [StateOptions, setstateOptions] = useState([]);
    const [selectedstateType, setSelectedstateType] = useState(null);

    const fetchStates = async () => {
        try {
            const response = await fetchData('SubContractAPI', `/State/${companyId}`, token);
            if (response.responseCode === '1111') {
                const options = response.response_data.map(state => ({
                    value: state.Id,
                    label: state.Name
                }));
                setstateOptions(options);
            }
        } catch (error) {
            console.error('Error fetching states:', error);
        }
    };
    
    const handlestateChange = (selectedOption) => {
        setSelectedstateType(selectedOption);
        setFormData(prevData => ({
            ...prevData,
            StateId: selectedOption ? selectedOption.value : '',
        }));
    };

    useEffect(() => {
        fetchStates();
    }, [companyId, token]);

    // Sync selected state when StateOptions and formData.StateId are both available
    useEffect(() => {
        if (StateOptions.length > 0 && formData.StateId) {
            const matchedState = StateOptions.find(option => option.value === formData.StateId);
            if (matchedState) {
                setSelectedstateType(matchedState);
            }
        }
    }, [StateOptions, formData.StateId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({  
            ...prevData,
            [name]: value
        }));
    };
    if (isLoadingData) {
        return (
            <div className="subcontract-page-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <div style={{ textAlign: 'center' }}>
                    <CircularProgress />
                    <p style={{ marginTop: '16px', color: '#64748b', fontSize: '14px' }}>Loading subcontract data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="subcontract-page-wrapper">
            {/* Header with Actions */}
            <div className="subcontract-page-header">
                <div className="header-left">
                    <h1 className="page-title">Edit SubContract</h1>
                    <p className="page-subtitle">Update subcontractor details</p>
                </div>
                <div className="header-actions">
                    <Button
                        variant="outlined"
                        component={Link}
                        to="/SubContractIndex"
                        className="btn-cancel"
                        sx={{ color: '#ffffff !important' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitData}
                        disabled={isLoading}
                        className="btn-save"
                        startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {isLoading ? "Updating..." : "Update"}
                    </Button>
                </div>
            </div>

            {/* Error Alert */}
            {errorMessage && (
                <div className="alert-error">
                    <svg className="alert-icon" fill="currentColor" viewBox="0 0 20 20" style={{ width: '18px', height: '18px' }}>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span>{errorMessage}</span>
                </div>
            )}

            {/* Success Alert */}
            {successMessage && (
                <div className="alert-success">
                    <svg className="alert-icon" fill="currentColor" viewBox="0 0 20 20" style={{ width: '18px', height: '18px' }}>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{successMessage}</span>
                </div>
            )}

            <div className="subcontract-content">
                {/* Single Unified Section */}
                <div className="form-section">
                    <div className="section-content">
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">
                                    Name <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="Name"
                                    className="form-control"
                                    value={formData.Name}
                                    onChange={handleChange}
                                    placeholder="Enter subcontractor name"
                                    onClick={(e) => e.target.select()}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    Contact Number <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="ContactNumber"
                                    className="form-control"
                                    value={formData.ContactNumber}
                                    onChange={handleChange}
                                    placeholder="Enter contact number"
                                    onClick={(e) => e.target.select()}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    State <span className="required">*</span>
                                </label>
                                <Select
                                    classNamePrefix="select"
                                    value={selectedstateType}
                                    onChange={handlestateChange}
                                    options={StateOptions}
                                    placeholder="Select state..."
                                    isSearchable
                                    isClearable
                                    menuPosition="fixed"
                                    menuPlacement="auto"
                                    styles={{
                                        control: (base, state) => ({
                                            ...base,
                                            minHeight: "38px",
                                            height: "38px",
                                            fontSize: "13px",
                                            border: "1.5px solid #e2e8f0",
                                            borderRadius: "8px",
                                            background: "#f8fafc",
                                            borderColor: state.isFocused ? '#667eea' : '#e2e8f0',
                                            boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.08)' : 'none',
                                            '&:hover': {
                                                borderColor: '#cbd5e1',
                                                background: '#ffffff',
                                            }
                                        }),
                                        valueContainer: (base) => ({
                                            ...base,
                                            height: "38px",
                                            padding: "2px 12px",
                                        }),
                                        indicatorsContainer: (base) => ({
                                            ...base,
                                            height: "38px",
                                        }),
                                        placeholder: (base) => ({
                                            ...base,
                                            color: "#94a3b8",
                                            fontSize: "12px",
                                        }),
                                        singleValue: (base) => ({
                                            ...base,
                                            color: "#1e293b",
                                            fontSize: "13px",
                                        }),
                                        menu: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                            borderRadius: "8px",
                                            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.12)",
                                            border: "1px solid #e2e8f0",
                                            position: "absolute",
                                        }),
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            fontSize: "13px",
                                            padding: "8px 12px",
                                            background: state.isSelected ? "#667eea" : state.isFocused ? "#f1f5f9" : "white",
                                            color: state.isSelected ? "white" : "#1e293b",
                                        }),
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SubContractEdit;

